import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';

import { BusinessAccountDto, BusinessAccountTypeNameMapping, CountryDto } from 'src/app/core/dtos/dto';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { AccountService } from 'src/app/core/services/account.service';
import { CountryRegionService } from 'src/app/core/services/country-region.service';

@Component({
  selector: 'app-edit-business-account-info',
  templateUrl: './edit-business-account-info.component.html',
  styleUrls: ['./edit-business-account-info.component.scss']
})
export class EditBusinessAccountInfoComponent implements OnInit {
  namePattern = '[\\u0000-\\u007f]+';

  businessAccountTypeNameMapping: BusinessAccountTypeNameMapping;
  businessAccount: BusinessAccountDto;
  countries: CountryDto[];
  userForm: FormGroup;
  submitted = false;
  isBusinessAccountEditable = true;

  constructor(
    private accountService: AccountService,
    private formBuilder: FormBuilder,
    private toastService: HotToastService,
    private countryRegionService: CountryRegionService,
    private router: Router,
  ) {

  }

  ngOnInit(): void {
    this.userForm = this.formBuilder.group({
      companyName: [{ value: '', disabled: true }, Validators.required],
      industry: [{ value: '', disabled: true }],
      contactFirstName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
      contactLastName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
      salutation: [''],
      email: [{ value: '', disabled: true }],
      phone: ['', [Validators.required, Validators.pattern("^[0-9]*$")]],
      countryId: [''],
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      postalCode: [''],
      region: [''],
    });
    this.getBusinessAccount();
    this.getCountries();
  }

  getBusinessAccount() {
    this.accountService.getBusinessAccount()
      .subscribe(result => {
        this.businessAccount = result.content.info;
        this.userForm.patchValue(this.businessAccount);
      },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            this.toastService.warning("Unable to load business account.");
          }
        });
  }

  getCountries() {
    this.countryRegionService.getCountries()
      .subscribe(result => {
        this.countries = result.content;
      },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            this.toastService.warning("Unable to load countries.");
          }
        });
  }

  onSubmit() {
    this.submitted = true;
    if (this.userForm.controls.phone.errors) {
      this.toastService.warning("Please enter a valid phone number");
      return;
    }
    if (this.userForm.invalid) {
      this.toastService.warning("Please fill in the missing fields");
      return;
    }
    const editRequest: BusinessAccountDto = {
      addressLine1: this.userForm.controls.addressLine1.value,
      addressLine2: this.userForm.controls.addressLine2.value,
      city: this.userForm.controls.city.value,
      companyName: this.userForm.controls.companyName.value,
      contactFirstName: this.userForm.controls.contactFirstName.value,
      contactLastName: this.userForm.controls.contactLastName.value,
      countryId: this.userForm.controls.countryId.value,
      email: this.userForm.controls.email.value,
      id: this.businessAccount.id,
      industry: this.userForm.controls.industry.value,
      phone: this.userForm.controls.phone.value,
      postalCode: this.userForm.controls.postalCode.value,
      region: this.userForm.controls.region.value,
      salutation: this.userForm.controls.salutation.value,
    };
    this.editBusinessAccount(editRequest);

  }

  editBusinessAccount(businessAccountForm: BusinessAccountDto) {
    this.accountService.editBusinessAccount(businessAccountForm)
      .subscribe(() => {
        this.toastService.success("Successful, Your business account information has been updated.");
        this.router.navigate(['my-account']);
      },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            if (errorMessage?.reasons) {
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
            else {
              this.toastService.warning("Failed to save personal information because of an unknown problem. Please contact Paragon.");
            }
          }
        });
  }
}
